import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, Tags, MessageSquare, ShoppingCart, Settings, LogOut, BookOpen, Menu, X, ExternalLink, Users, ShieldCheck, GraduationCap } from "lucide-react";

const ROLE_LABELS = { super_admin: "Super Admin", manager: "Manager", staff: "Staff" };

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["super_admin", "manager", "staff"] },
  { to: "/admin/products", label: "Products", icon: Package, roles: ["super_admin", "manager", "staff"] },
  { to: "/admin/categories", label: "Categories", icon: Tags, roles: ["super_admin", "manager", "staff"] },
  { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare, roles: ["super_admin", "manager", "staff"] },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart, roles: ["super_admin", "manager", "staff"] },
  { to: "/admin/schools", label: "School Lists", icon: GraduationCap, roles: ["super_admin", "manager", "staff"] },
  { to: "/admin/users", label: "Admin Users", icon: Users, roles: ["super_admin"] },
  { to: "/admin/settings", label: "Settings", icon: Settings, roles: ["super_admin", "manager"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
        <span className="w-9 h-9 flex items-center justify-center bg-brand-gold rounded-md">
          <BookOpen className="w-5 h-5 text-brand-blue" strokeWidth={2.5} />
        </span>
        <span className="font-display font-black text-lg text-white">BooksNmore</span>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.filter((n) => n.roles.includes(user?.role)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-brand-gold text-black" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white" data-testid="admin-view-site">
          <ExternalLink className="w-4 h-4" /> View website
        </Link>
        <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-white/70 hover:bg-red-500/20 hover:text-white">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-brand-blue flex-col z-40">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-brand-blue flex flex-col"><SidebarContent /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b flex items-center justify-between px-4 h-16">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu className="w-5 h-5" /></button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-slate-500">Signed in as</span>
            <span className="font-semibold text-brand-blue">{user?.username}</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold bg-brand-gold text-black px-2 py-1 rounded-full" data-testid="admin-role-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> {ROLE_LABELS[user?.role] || user?.role}
            </span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
